<script setup lang="ts">
/**
 * 部署清單（`/deployments`；operator 可見）。
 *
 * 過濾器的兩層原則（同 `JobsView.vue:2-10`）：
 * - **下拉送伺服器**：客戶／代理商／產品／代管主機下拉走 `?customer_id=`／`?reseller_id=`／`?product=`／`?host_id=`，真的縮小結果集
 *   （後端先把 host 換成在役站台的 instance_id、把客戶換成 license_id 再 IN）。
 * - **桶留前端**：型態（全部／SaaS／代管／自部署）與狀態（在線／離線）是同一批資料的分類，
 *   計數要一次算齊，送伺服器反而要多打幾次。
 *
 * ⚠️ **operator 坑**：客戶下拉的來源 `GET /customers` 是 `require_admin`——所以下拉本體與
 *    載入都掛 `v-if="auth.isAdminOrAbove"`。少了這道，operator 一開頁就 403 白屏。
 *    代管主機下拉的 `GET /hosts` 與產品憑證設計 `GET /products`（批93 收尾放寬到
 *    `require_any_admin_user`）operator 都讀得到，不受此限。
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import * as hosting from '@/api/hosting'
import type { ManagedHost } from '@/api/hosting'
import type { Deployment, MetricDefinition } from '@/api/ls'
import FilterClearButton from '@/components/FilterClearButton.vue'
import MetricDefEditor from '@/components/MetricDefEditor.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import UiButton from '@/components/UiButton.vue'
import UiMeter from '@/components/UiMeter.vue'
import UiModal from '@/components/UiModal.vue'
import { meterLevel } from '@/lib/fleet'
import { deploymentKind, type DeploymentKind } from '@/lib/deploymentKind'
import { buildMetricRows } from '@/lib/metrics'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { buildQuotaRows, planLabel, worstUsagePct } from '@/lib/quotas'
import { DEPLOY_OFFLINE_HOURS, fmtSeen, isDeploymentOffline, staleLevel } from '@/lib/staleness'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const store = useDataStore()

// --- 型態桶（前端）常數：要在 `usePersistedFilters` 之前宣告（oneOf 用得到）---
// 批171：多了「SaaS」（多租戶平台旗下的組織）。它們沒有代管主機，之前被算進「自部署」。
// 判準集中在 `lib/deploymentKind.ts`，一筆只落一個桶。
type Kind = 'all' | DeploymentKind
const KIND_FILTERS: { key: Kind; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'saas', label: 'SaaS' },
  { key: 'managed', label: '代管' },
  { key: 'self', label: '自部署' },
]
type Liveness = 'all' | 'online' | 'offline'
type SortMode = 'created' | 'usage'

// --- 過濾條件（批186：記住）。前四個送伺服器，後三個留前端 ---
// `custFilter`／`hostFilter`：客戶／代管主機 id；`resFilter`（批107）：`''`＝全部、`'none'`＝直營、uuid＝那一家；
// `productFilter`（批171）：產品 key，選項來自憑證設計清單（operator 也讀得到）。
const pf = usePersistedFilters(
  'deployments',
  {
    custFilter: '',
    hostFilter: '',
    resFilter: '',
    productFilter: '',
    kind: 'all' as Kind,
    liveness: 'all' as Liveness,
    sortMode: 'created' as SortMode,
  },
  {
    oneOf: {
      kind: KIND_FILTERS.map((f) => f.key),
      liveness: ['all', 'online', 'offline'],
      sortMode: ['created', 'usage'],
    },
  },
)
const { custFilter, hostFilter, resFilter, productFilter, kind, liveness, sortMode } = pf.refs
const { dirty: filtersDirty } = pf
/** `watch` 會在條件變動時重載，所以清除只要改值。 */
const clearFilters = () => pf.clear()
const hosts = ref<ManagedHost[]>([])
/** 輪到「在線／離線」時的判準基準點——載入時更新一次就好（本頁不輪詢）。 */
const now = ref(Date.now())

async function load() {
  await store.loadDeployments({
    customerId: custFilter.value || undefined,
    hostId: hostFilter.value || undefined,
    resellerId: resFilter.value || undefined,
    product: productFilter.value || undefined,
  })
  now.value = Date.now()
}

/**
 * keyset 分頁（批93-D LS-4）：後端一頁 50 筆，`store.deploymentsNextCursor` 非 null＝還有。
 *
 * ★ 型態桶（全部／SaaS／代管／自部署）與在線／離線都是**前端**算的，所以「載入更多」載的是
 *   下一頁**原始資料**、不是下一頁的過濾結果——與 `LicensesView` 同一個行為，桶的計數
 *   也因此是「已載入的那些」的計數。
 */
const loadingMore = ref(false)
async function loadMore() {
  if (!store.deploymentsNextCursor || loadingMore.value) return
  loadingMore.value = true
  try {
    await store.loadMoreDeployments()
  } finally {
    loadingMore.value = false
  }
}

onMounted(() => {
  void load()
  // 代管主機下拉：operator 也讀得到（`GET /hosts` 沒有 admin gate）。
  void hosting
    .listManagedHosts()
    .then((rows) => {
      hosts.value = rows
    })
    .catch(() => {})
  // 憑證設計：operator 也讀得到（批93 收尾把 `GET /products` 放寬到
  // `require_any_admin_user`）——配額對話框要靠它把配額鍵翻成中文。
  void store.ensureProducts().catch(() => {})
  // 批112：指標的顯示定義（`GET /metric-defs` 是 `require_staff`，operator 也讀得到）。
  // 載不到就退化成原始英文鍵——`lib/metrics.ts` 的 defs=null 分支會接手。
  void store.ensureMetricDefs().catch(() => {})
  // ★ 客戶與代理商清單只在 admin+ 載（見檔頭的 operator 坑）。
  if (auth.isAdminOrAbove) {
    void store.ensureResellers()
    void store.ensureCustomers().catch(() => {})
  }
})

/**
 * 這筆部署該用哪個產品的憑證設計來解讀配額鍵（批93）。
 *
 * 直接讀部署列的 `product`（後端由 `license_id` 推回，同一次 IN 批載）。null＝孤兒部署
 * 或憑證設計還沒載回來 → `lib/quotas.ts` 退化成印原始英文 key，不猜別的產品。
 */
function specFor(d: Deployment) {
  return store.productByKey(d.product)
}
watch([custFilter, hostFilter, resFilter, productFilter], () => void load())

// --- 型態桶（前端；常數與 `kind` 在檔頭）---
const kindCounts = computed(() => {
  const c: Record<Kind, number> = { all: store.deployments.length, saas: 0, managed: 0, self: 0 }
  for (const d of store.deployments) c[deploymentKind(d)]++
  return c
})

// --- 排序（前端，批169 裁示 81）---
//
// 「依使用率」＝每筆部署**最高**的配額使用率（與按鈕前那顆色點同一個數字，`worstUsagePct`），
// 高的在前；算不出來的（不限／從未回報／孤兒部署）排最後、彼此維持原順序。
//
// ★ **只排已載入的列**（擇一的那一個，不另開不分頁模式）：
//   ① 使用率要用憑證設計（`absent_default`、`usage_source`、`usage_metric`）＋合併子證後的上限
//      在前端算——伺服器端排序得先把**全部**部署的上限與子證算一遍，等於把分頁拆掉；
//   ② 清單的 keyset 游標鍵是不可變的 `created_at`（見後端 `list_deployments`），換成會變的使用率
//      當排序鍵就是「翻頁當下用量變了就漏一台」的老問題；
//   ③ 部署數是每站一筆，現況一頁（50 筆）就載完；還有下一頁時畫面明講「只排已載入的」，要看全部
//      就按〈載入更多〉。
// （`sortMode` 與過濾條件一起宣告在檔頭。）

/** 這筆部署的有效上限：主證＋生效子證（批169 起後端算好；舊版 LS 沒有這一欄就退回主證）。 */
function limitsOf(d: Deployment) {
  return d.limits?.effective_quotas ?? d.limits?.quotas ?? null
}

const rows = computed(() => {
  const filtered = store.deployments.filter((d) => {
    if (kind.value !== 'all' && deploymentKind(d) !== kind.value) return false
    if (liveness.value === 'all') return true
    const offline = isDeploymentOffline(d.last_seen, now.value)
    return liveness.value === 'offline' ? offline : !offline
  })
  if (sortMode.value !== 'usage') return filtered
  const keyed = filtered.map((d, i) => ({ d, i, p: worstPct(d) }))
  keyed.sort((a, b) => {
    if (a.p == null && b.p == null) return a.i - b.i
    if (a.p == null) return 1
    if (b.p == null) return -1
    return b.p - a.p || a.i - b.i
  })
  return keyed.map((x) => x.d)
})

// --- 配額用量（批81 需求 6，裁示 12）---
// 舊版是一格塞十顆「已用」膠囊：只有分子沒有分母，看不出「快滿了沒」，還把表格撐爆。
// 改成一顆小按鈕＋最高佔用率色點，細節（每項一條彩色橫條）進對話框。

/** 佔用率（%）。上限 null（不限）或從未回報 → null＝算不出來，不參與色階。 */
function pctOf(used: number | null | undefined, limit: number | null): number | null {
  if (limit == null || used == null) return null
  // 上限 0（舊授權缺該配額鍵、憑證設計的 `absent_default` 解讀成 0）：用了就是滿的。
  if (limit <= 0) return used > 0 ? 100 : 0
  return (used / limit) * 100
}

/**
 * 全部配額裡最高的那個佔用率；全部算不出來（含無授權對照）→ null。
 *
 * ★ 批114：原本這裡多算了一筆「證上那個獨立數字欄」的佔用率。那個鍵批109 起就住在
 *   `quotas` 裡，`buildQuotaRows` 已經含它——留著等於同一鍵算兩次。
 * ★ 批169：上限改用**合併子證後**的有效上限（`limitsOf`），算法搬進 `lib/quotas.ts::worstUsagePct`
 *   （排序與色點吃同一份；用量優先讀精確指標 `usage_metric`）。
 */
function worstPct(d: Deployment): number | null {
  if (!d.limits) return null
  return worstUsagePct(specFor(d), limitsOf(d), d)
}

/** 按鈕前的色點：沿用水位色階（80% 橘／90% 紅）；`na`＝沒有可計算的上限，灰。 */
function worstLevel(d: Deployment): string {
  const p = worstPct(d)
  return p == null ? 'na' : meterLevel(p)
}

const openRow = ref<Deployment | null>(null)
const dlgTitle = computed(() => {
  const d = openRow.value
  if (!d) return '用量與指標'
  return `用量與指標 — ${d.instance_name || d.hostname || d.instance_id.slice(0, 12)}`
})
/**
 * 對話框的列——**全部**由 `buildQuotaRows` 產生（批109）。
 *
 * 在這之前，證上有一個獨立的一級數字欄，這裡得自己補一列成同形的 `QuotaRow`
 * （portal 的 `PlanPicker` 與 `LicensesView` 各抄了一份同樣的 hack）。那個欄併進
 * `quotas` 之後，它就是憑證設計裡的一個配額鍵，欄序、標籤、`absent_default`
 * 與用量來源（`usage_source`）全部由 spec 驅動。
 */
const dlgRows = computed(() => {
  const d = openRow.value
  if (!d?.limits) return []
  return buildQuotaRows(specFor(d), limitsOf(d), d).map((r) => ({
    ...r,
    // 顯示用百分比與 `worstPct` 同一套算法，只是封頂 100（橫條不能爆出去）。
    pct: Math.min(100, pctOf(r.used, r.limit) ?? 0),
    /** 上限 null＝不限、已用 null＝從未回報：兩者都灰字、橫條 0%。 */
    dim: r.limit == null || r.used == null,
  }))
})

// --- 站台指標（批112）---
//
// ★★ 指標區段**不受** `openRow.limits` 保護：指標與授權無關（孤兒部署——證被刪了——
//    照樣在回報指標）。上面的配額區段沒有上限就畫不出來，這裡沒有這個依賴。

/** 已定義的在前（依 `sort_order`）、未定義的在後；`hidden` 的定義整列濾掉。 */
const metricRows = computed(() =>
  buildMetricRows(store.metricDefs, openRow.value?.product ?? null, openRow.value?.metrics),
)
const knownMetrics = computed(() => metricRows.value.filter((r) => !r.unknown))
const unknownMetrics = computed(() => metricRows.value.filter((r) => r.unknown))

/**
 * 指標時間**只在與最後上線差超過一小時時才顯示**。
 *
 * 兩個時間戳刻意分開（站台可能持續心跳但不送指標），但正常情況下它們只差幾毫秒——
 * 每次都印一行「指標時間」只會讓人以為那是兩件不同的事。差得夠遠才是真的要說的話：
 * 「這台還活著，但指標停在某個時間點沒再更新了」。
 */
const METRICS_STALE_MS = 60 * 60 * 1000
const metricsStale = computed(() => {
  const d = openRow.value
  if (!d?.metrics_at) return false
  return Math.abs(Date.parse(d.last_seen) - Date.parse(d.metrics_at)) > METRICS_STALE_MS
})

/**
 * 「＋ 建立顯示定義」：**就地**開 `MetricDefEditor`（`product`／`key` 預填唯讀）。
 *
 * ★ 刻意不跳 `/metric-defs`：營運的動線是「看到一個不認識的鍵 → 補名字 → 繼續看這台站
 *   的數字」，跳頁再跳回來會把它斷成兩段（而且回來時對話框已經關了）。那條
 *   `?product=&key=` 的 query 仍然留著給別的入口用。
 */
const defEditorOpen = ref(false)
const defEditorKey = ref<string | null>(null)

function defineMetric(key: string) {
  if (!openRow.value?.product) return
  defEditorKey.value = key
  defEditorOpen.value = true
}

/**
 * 底層（用量與指標）對話框的關閉。★ 編輯窗開著時**一律忽略**——`UiModal` 的 Esc 監聽是
 * 每一層各自掛在 window 上的，按一次 Esc 兩層都會收到；不擋的話關掉上層的同時底層也沒了。
 */
function closeUsage() {
  if (defEditorOpen.value) return
  openRow.value = null
}

function closeDefEditor() {
  defEditorOpen.value = false
  defEditorKey.value = null
  // `UiModal` 卸載時會把 `body.overflow` 還原——但底層那個還開著，要補回去，
  // 否則關掉編輯窗之後背景頁面就能捲動了。
  // ★ **一定要等 `nextTick`**：上面那行只是改 ref，`UiModal` 的 `watch` 還沒跑；
  //   同步補 `hidden` 會在幾毫秒後被它的 `detach()` 清掉（實測過）。
  void nextTick(() => {
    if (openRow.value) document.body.style.overflow = 'hidden'
  })
}

/**
 * 存檔成功：**就地更新** `store.metricDefs`，那一列立刻從「未定義指標」移到上面的
 * 指標區（`metricRows` 是 computed，改陣列就會重算）——不重抓整份，畫面也不閃。
 */
function onDefSaved(def: MetricDefinition) {
  const i = store.metricDefs.findIndex((d) => d.product === def.product && d.key === def.key)
  if (i >= 0) store.metricDefs[i] = def
  else store.metricDefs.push(def)
  store.metricDefsLoaded = true
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>部署</h1>
      <span class="count">{{ store.deployments.length }}</span>
      <span class="spacer" />
      <label v-if="auth.isAdminOrAbove" class="hf">
        <span>客戶</span>
        <select v-model="custFilter">
          <option value="">全部客戶</option>
          <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
        </select>
      </label>
      <!-- 批107：代理商過濾。★ 與客戶下拉同一個理由只給 admin+——`GET /resellers`
           是 `require_admin`，operator 載不到清單（列上的代理商欄位他仍看得到）。 -->
      <label v-if="auth.isAdminOrAbove" class="hf">
        <span>代理商</span>
        <select v-model="resFilter">
          <option value="">全部</option>
          <option value="none">直營</option>
          <option v-for="r in store.resellers" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
      </label>
      <!-- 批171：產品過濾。★ operator 也看得到——`GET /products` 批93 收尾放寬到 `require_any_admin_user`。 -->
      <label class="hf">
        <span>產品</span>
        <select v-model="productFilter">
          <option value="">全部產品</option>
          <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
        </select>
      </label>
      <label class="hf">
        <span>代管主機</span>
        <select v-model="hostFilter">
          <option value="">全部主機</option>
          <option v-for="h in hosts" :key="h.id" :value="h.id">{{ h.name }}</option>
        </select>
      </label>
      <label class="hf">
        <span>狀態</span>
        <select v-model="liveness" :title="`離線＝超過 ${DEPLOY_OFFLINE_HOURS} 小時沒有心跳`">
          <option value="all">全部</option>
          <option value="online">在線</option>
          <option value="offline">離線</option>
        </select>
      </label>
      <!-- 批169：依配額使用率排序（前端、只排已載入的列，理由見 `sortMode` 的註解）。 -->
      <label class="hf">
        <span>排序</span>
        <select v-model="sortMode" title="使用率＝這台所有配額裡最高的已用／上限（含加購子證）">
          <option value="created">最新加入</option>
          <option value="usage">使用率（高→低）</option>
        </select>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
    </div>

    <div class="card">
      <div class="tabs filters" data-hscroll>
        <button
          v-for="f in KIND_FILTERS"
          :key="f.key"
          type="button"
          class="tab"
          :class="{ active: kind === f.key }"
          @click="kind = f.key"
        >
          {{ f.label }}<span class="tab-n">{{ kindCounts[f.key] }}</span>
        </button>
      </div>

      <div v-if="!rows.length" class="empty">
        <div class="empty-sub">
          {{ store.deployments.length ? '這個分類目前沒有部署。' : '尚無部署實例回報。' }}
        </div>
      </div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>客戶</th><th>代理商</th><th>主機</th><th>代管主機</th><th>授權</th><th>版本</th>
              <th>對外 IP</th><th>配額</th><th>配額用量</th><th>狀態</th><th>認證</th><th>最後上線</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in rows" :key="d.instance_id">
              <td>{{ d.customer_name ?? '—' }}</td>
              <!-- 批107：這台回報的那張證的代理商快照。空＝直營（或孤兒部署）。 -->
              <td class="nowrap">
                <span v-if="d.reseller_name" class="chip">{{ d.reseller_name }}</span>
                <span v-else class="subtle" title="我方直營（這張證上沒有代理商）">直營</span>
              </td>
              <td>
                <div class="hostcell">
                  <span>{{ d.instance_name || d.hostname || '—' }}</span>
                  <code class="mono">{{ d.instance_id.slice(0, 16) }}</code>
                </div>
              </td>
              <td class="nowrap">
                <!-- 批171：平台組織沒有代管主機，標出是哪個 SaaS 平台的（之前會被當成自部署畫「—」）。 -->
                <span
                  v-if="d.platform_id"
                  class="chip"
                  :title="`多租戶平台旗下的組織，由「${d.platform_name ?? ''}」代為取證與回報`"
                >SaaS · {{ d.platform_name ?? d.platform_id.slice(0, 8) }}</span>
                <RouterLink
                  v-else-if="d.managed_host_id"
                  :to="{ name: 'host-detail', params: { id: d.managed_host_id } }"
                >
                  {{ d.managed_host_name ?? d.managed_host_id.slice(0, 8) }}
                </RouterLink>
                <span v-else class="subtle" title="自部署（客戶自己的機器，不在我方代管中台）">—</span>
              </td>
              <td><code class="mono">{{ d.license_id.slice(0, 8) }}</code></td>
              <td>{{ d.app_version ?? '—' }}</td>
              <td class="nowrap">
                <code v-if="d.last_ip" class="mono">{{ d.last_ip }}</code>
                <span v-else class="subtle">—</span>
              </td>
              <!--
                批114：原本是兩行硬編的「某產品的兩個數字欄」，而且只有分子沒有分母。
                改成資料驅動的摘要——這一列本來就帶著上限（`d.limits.quotas`）與心跳用量
                （扁平的 `*_in_use` 欄＋`quotas_in_use`，`QuotaSummary` 內部自己正規化），
                所以這裡顯示得出「已用／上限」。孤兒部署（`limits` 為 null）顯示「—」。
              -->
              <td class="quota">
                <QuotaSummary :spec="specFor(d)" :quotas="limitsOf(d)" :usage="d" />
              </td>
              <td class="usage">
                <UiButton size="sm" variant="secondary" @click="openRow = d">
                  <span class="dot" :class="worstLevel(d)" aria-hidden="true" />配額用量 ▸
                </UiButton>
              </td>
              <td>
                <span class="status-pill" :class="d.conflict_flag ? 'revoked' : 'active'">
                  {{ d.conflict_flag ? '衝突' : '正常' }}
                </span>
              </td>
              <!-- 批93 §2.4：心跳帶不帶得出簽章 blob。未認證＝舊版 CS 還沒升級，
                   degrade 模式下那些心跳**不寫任何欄位**（用量與最後上線都會停在舊值）。 -->
              <td>
                <span
                  class="status-pill"
                  :class="d.blob_verified_at ? 'active' : 'pending'"
                  :title="
                    d.blob_verified_at
                      ? `最後一次通過心跳驗章：${fmtSeen(d.blob_verified_at)}`
                      : '這台從未通過心跳驗章（舊版站台未帶授權 blob）——相容期間它的用量與最後上線不會更新'
                  "
                >
                  {{ d.blob_verified_at ? '已認證' : '未認證' }}
                </span>
              </td>
              <td class="seen" :class="staleLevel(d.last_seen)">{{ fmtSeen(d.last_seen) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- keyset 分頁：後端一頁 50 筆。`next_cursor` 非 null 才畫這一列。 -->
      <div v-if="store.deploymentsNextCursor" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">
          已載入 {{ store.deployments.length }} 筆{{ sortMode === 'usage' ? '（使用率排序只涵蓋已載入的部署）' : '' }}
        </span>
      </div>
    </div>

    <!-- 用量與指標對話框：上段配額（裁示 12，每項一條彩色橫條「已用 / 上限」）、
         下段站台指標（批112）。★★ 兩段的前提不同——配額要有授權才算得出上限，
         指標與授權無關（孤兒部署照樣在回報），所以指標段**不在** `limits` 的 v-if 裡。 -->
    <UiModal :open="!!openRow" :title="dlgTitle" width="560px" @close="closeUsage">
      <template v-if="openRow">
        <h4 class="dlg-sec">配額用量</h4>
        <div v-if="!openRow.limits" class="nolim">
          找不到這筆部署對應的授權（證已刪除或部署列比證還舊），無法顯示上限。
        </div>
        <template v-else>
          <div class="dlg-plan">
            <span class="mk">授權套裝</span>
            <span class="mv">{{ planLabel(specFor(openRow), openRow.limits.plan) }}</span>
            <code class="mono">{{ openRow.license_id.slice(0, 8) }}</code>
          </div>
          <div class="qrows">
            <div v-for="r in dlgRows" :key="r.key" class="qrow" :class="{ dim: r.dim }">
              <span class="qk">{{ r.label }}</span>
              <UiMeter class="qm" :pct="r.pct" />
              <span class="qv">{{ r.text }}</span>
            </div>
          </div>
        </template>

        <h4 class="dlg-sec">站台指標</h4>
        <div v-if="!openRow.metrics_at" class="nolim">
          這台還沒有回報過指標（舊版站台，或產品端沒有送）。
        </div>
        <template v-else>
          <p v-if="metricsStale" class="subtle mstale">
            指標時間 {{ fmtSeen(openRow.metrics_at) }}（與最後上線不同——這台仍在心跳，
            但已經有一段時間沒有更新指標了）
          </p>
          <div v-if="knownMetrics.length" class="mrows">
            <div v-for="r in knownMetrics" :key="r.key" class="mrow" :title="r.hint ?? ''">
              <span class="mk2">{{ r.label }}</span>
              <span class="mv2">{{ r.text }}</span>
            </div>
          </div>
          <p v-else-if="!unknownMetrics.length" class="subtle">
            這一發回報「沒有指標」（站台明確送了空的一包）。
          </p>
          <!-- 未定義的鍵：摺疊起來但**永遠列出**——不列的話沒有人會知道有新鍵進來。
               發現機制就是這裡（前端已有 metricDefs 與當頁 deployments），不另開端點。 -->
          <details v-if="unknownMetrics.length" class="unk">
            <summary>未定義指標（{{ unknownMetrics.length }}）</summary>
            <div v-for="r in unknownMetrics" :key="r.key" class="mrow">
              <span class="mk2"><code class="mono">{{ r.key }}</code></span>
              <span class="mv2">{{ r.text }}</span>
              <UiButton
                v-if="auth.isManagerOrAbove && openRow.product"
                size="sm"
                variant="secondary"
                @click="defineMetric(r.key)"
              >
                ＋ 建立顯示定義
              </UiButton>
            </div>
          </details>
        </template>
      </template>
      <template #foot>
        <span v-if="openRow" class="foot-seen seen" :class="staleLevel(openRow.last_seen)">
          回報時間 {{ fmtSeen(openRow.last_seen) }}
        </span>
        <UiButton @click="openRow = null">關閉</UiButton>
      </template>
    </UiModal>

    <!-- 指標顯示定義的編輯窗（批112）：**疊在上面那層之上**（同一個 z-index，宣告在後
         就畫在上面），存檔後底層那張對話框原封不動，那一列直接從「未定義」移上去。
         元件與 `/metric-defs` 那一頁共用同一支，不在這裡另做一份表單。 -->
    <MetricDefEditor
      :open="defEditorOpen"
      :preset-product="openRow?.product ?? null"
      :preset-key="defEditorKey"
      @close="closeDefEditor"
      @saved="onDefSaved"
    />
  </div>
</template>

<style scoped>
/* keyset 分頁的「載入更多」列（形狀與 LicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
  flex-wrap: wrap;
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.filters {
  margin-bottom: var(--sp-3);
}
.tab-n {
  margin-left: 6px;
  font-size: var(--fs-11);
  font-weight: 500;
  color: var(--fg-4);
}
.tab.active .tab-n {
  color: inherit;
}
.hostcell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
/* 批114：配額摘要欄。`<QuotaSummary>` 自己 nowrap＋省略號，這裡只壓欄寬。 */
.quota {
  max-width: 170px;
}
.nowrap {
  white-space: nowrap;
}
td a {
  color: var(--brand-700);
  font-weight: 600;
  text-decoration: none;
}
[data-theme='dark'] td a {
  color: var(--brand-400);
}
td a:hover {
  text-decoration: underline;
}
/* `.sub` 只給 td **內**的 div／span（套在 td 本身會讓儲存格脫離表格列）。 */
.sub {
  font-size: var(--fs-11);
  color: var(--fg-3);
  margin-top: 2px;
}
.usage {
  white-space: nowrap;
}
/* 按鈕前的最高佔用率色點：`na`＝沒有可計算的上限（灰）。 */
.dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 50%;
  background: var(--success);
  flex: none;
}
.dot.warn {
  background: var(--warning);
}
.dot.bad {
  background: var(--danger);
}
.dot.na {
  background: var(--fg-4);
}
.nolim {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.dlg-plan {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
  font-size: var(--fs-13);
}
.dlg-plan .mk {
  color: var(--fg-3);
}
.dlg-plan .mv {
  color: var(--fg);
  font-weight: 600;
}
.qrows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.qrow {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
}
.qk {
  width: 96px;
  flex: none;
  color: var(--fg-2);
}
.qm {
  flex: 1;
  min-width: 0;
}
.qv {
  width: 110px;
  flex: none;
  text-align: right;
  color: var(--fg-2);
  font-variant-numeric: tabular-nums;
}
/* 不限／從未回報：橫條是 0%，數字也不該看起來像「用了 0」。 */
.qrow.dim .qv {
  color: var(--fg-4);
}
.foot-seen {
  margin-right: auto;
  font-size: var(--fs-12);
}
/* 批112：對話框兩段式的區段標題。第一段不要上緣間距（緊接 modal 標題）。 */
.dlg-sec {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg-2);
}
.dlg-sec:first-child {
  margin-top: 0;
}
.mstale {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.mrows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.mrow {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
}
.mk2 {
  flex: 1;
  min-width: 0;
  color: var(--fg-2);
}
.mv2 {
  flex: none;
  text-align: right;
  color: var(--fg);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.unk {
  margin-top: var(--sp-3);
  font-size: var(--fs-12);
}
.unk summary {
  cursor: pointer;
  color: var(--fg-3);
}
.unk > .mrow {
  margin-top: 6px;
}
</style>
